import { t } from '@hefe/i18n';
import {
  BasketballEventType,
  BasketballEventUserRoleType,
  BasketballTeamBasic,
  BasketballTeamPlayerReplies,
  BasketballTeamPlayerReply,
  EventReplies,
} from '@hefe/models';
import { formatDateTime } from '@hefe/utils';
import { Col, Collapse, List, Row, Space, Typography } from 'antd';
import { FaCheck, FaQuestion, FaTimes } from 'react-icons/fa';
import CustomList from '../../../../core/components/custom-list/custom-list.component';
import UserAvatar from '../../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import EventReplyComponent from '../../event-reply/event-reply.component';

interface EventDetailsRepliesComponentProps {
  allowViewReplies?: boolean;
  replies?: BasketballTeamPlayerReplies | EventReplies;
  isCoach: boolean;
  eventType: BasketballEventType;
  eventId: number;
  team: BasketballTeamBasic;
}

export default function EventDetailsRepliesComponent(
  props: EventDetailsRepliesComponentProps
) {
  const { allowViewReplies, replies } = props;

  if (!allowViewReplies || !replies) {
    return null;
  }

  const countAlign = 'start';
  const countIonSize = 14;

  const countYes = replies.yes.length;
  const countNo = replies.no.length;
  const countUnknown = replies.unknown.length;
  const countNotNominated = replies.not_nominated.length;
  const defaultActiveKey: string[] = [];
  if (countYes) defaultActiveKey.push('1');
  if (countNo) defaultActiveKey.push('2');
  if (countUnknown) defaultActiveKey.push('3');

  const ListItem = (item: BasketballTeamPlayerReply) => {
    const updatedAt = item.reply?.updated_at;
    const reason = item.reply?.reason;
    const text: string[] = [];

    if (updatedAt) {
      text.push(
        t('last_updated') +
          ': ' +
          formatDateTime(updatedAt as string, { withHypens: true })
      );
    }
    if (reason) {
      text.push(reason);
    }

    const actions: any[] = [];

    if (props.isCoach && item.nominated) {
      actions.push(
        <EventReplyComponent
          size="small"
          event={{
            description: '',
            end: '',
            location: '',
            teams: [],
            type: props.eventType,
            date: '',
            user_data: [],
            id: props.eventId,
            status: 0,
            primary_role: {
              user_id: 0,
              type: BasketballEventUserRoleType.PLAYER,
            },
          }}
          eventRole={{
            type: BasketballEventUserRoleType.PLAYER,
            team_id: props.team?.id,
            team: props.team?.name,
            reply: item.reply,
            nominated: item.nominated,
          }}
          userId={item.id}
        />
      );
    }

    const description = text.join(' | ');
    return (
      <List.Item style={{ paddingTop: 5, paddingBottom: 5 }} actions={actions}>
        <List.Item.Meta
          avatar={<UserAvatar size="default" user={item} />}
          title={item.firstname + ' ' + item.lastname}
          description={description}
        />
      </List.Item>
    );
  };

  return (
    <Row>
      <Col
        xs={24}
        style={{ textAlign: 'center', marginTop: 16, marginBottom: 16 }}
      >
        <Space align={countAlign}>
          <Space align={countAlign}>
            <FaCheck size={countIonSize} />
            <Typography.Text>{countYes}</Typography.Text>
          </Space>
          <Typography.Text>/</Typography.Text>
          <Space align={countAlign}>
            <FaTimes size={countIonSize} />
            <Typography.Text>{countNo}</Typography.Text>
          </Space>
          <Typography.Text>/</Typography.Text>
          <Space align={countAlign}>
            <FaQuestion size={countIonSize} />
            <Typography.Text>{countUnknown}</Typography.Text>
          </Space>
        </Space>
      </Col>
      <Col xs={24}>
        <Collapse defaultActiveKey={defaultActiveKey}>
          <Collapse.Panel key="1" header={`${t('commitments')} (${countYes})`}>
            <CustomList
              itemLayout="horizontal"
              dataSource={replies.yes}
              renderItem={(item: BasketballTeamPlayerReply) => ListItem(item)}
            />
          </Collapse.Panel>
          <Collapse.Panel key="2" header={`${t('cancellations')} (${countNo})`}>
            <CustomList
              itemLayout="horizontal"
              dataSource={replies.no}
              renderItem={(item: BasketballTeamPlayerReply) => ListItem(item)}
            />
          </Collapse.Panel>
          <Collapse.Panel key="3" header={`${t('pending')} (${countUnknown})`}>
            <CustomList
              itemLayout="horizontal"
              dataSource={replies.unknown}
              renderItem={(item: BasketballTeamPlayerReply) => ListItem(item)}
            />
          </Collapse.Panel>
          {countNotNominated > 0 && (
            <Collapse.Panel
              key="4"
              header={`${t('nominees.not_nominated')} (${countNotNominated})`}
            >
              <CustomList
                itemLayout="horizontal"
                dataSource={replies.not_nominated}
                renderItem={(item: BasketballTeamPlayerReply) => ListItem(item)}
              />
            </Collapse.Panel>
          )}
        </Collapse>
      </Col>
    </Row>
  );
}
